import { FC, memo, useEffect, useMemo, useState } from "react";

import { Translate } from "@/app/application/Translate";
import {
	Box,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";

interface LiveValueReadoutProps {
	baseValue: number;
	oscillation: number;
	amplitude: number;
}

const LiveValueReadout: FC<LiveValueReadoutProps> = ({ baseValue, oscillation, amplitude }) => {
	const [currentValue, setCurrentValue] = useState<number>(baseValue);

	useEffect(() => {
		if (amplitude === 0) {
			setCurrentValue(baseValue);
			return;
		}

		const compute = () => waveValue(baseValue, amplitude, oscillation, Date.now() / 1000);
		setCurrentValue(compute());
		const interval = setInterval(() => setCurrentValue(compute()), 200);

		return () => clearInterval(interval);
	}, [baseValue, oscillation, amplitude]);

	return (
		<Stack direction="row" alignItems="baseline" justifyContent="flex-end" gap={0}>
			<Typography variant="body1" color="text.secondary">
				<Translate String="Live.EditTag.CurrentValue">
					Current Value
				</Translate>
				:
			</Typography>
			<Typography
				variant="h5"
				fontWeight={700}
				sx={{ fontVariantNumeric: "tabular-nums", minWidth: "8ch", textAlign: "right" }}
			>
				{currentValue.toFixed(2)}
			</Typography>
		</Stack>
	);
};

interface EditedLiveTagValuePreviewProps {
	baseValue: number;
	oscillation: number;
	amplitude: number;
}

const EditedLiveTagValuePreview: FC<EditedLiveTagValuePreviewProps> = memo(({ baseValue, oscillation, amplitude }) => {
	const theme = useTheme();
	const chartData = useWaveformData(baseValue, oscillation, amplitude);

	return (
		<Box sx={{ height: "100%", display: "grid", gridTemplateRows: "1fr auto", gap: 1 }}>
			<Box sx={{ bgcolor: "action.hover", borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", mx: 1.5, my: 3, px: 3, minHeight: 200 }}>
				<LineChart
					series={[
						{
							data: chartData.yData,
							area: false,
							showMark: false,
							color: theme.palette.primary.main,
						},
					]}
					xAxis={[
						{
							data: chartData.xData,
							label: "Time (s)",
							valueFormatter: (v: number) => `${v.toFixed(1)}s`,
						},
					]}
					margin={{  top: 40, right: 20, bottom: 60, left: 40 }}
					skipAnimation
				/>
			</Box>
			<LiveValueReadout
				baseValue={baseValue}
				oscillation={oscillation}
				amplitude={amplitude}
			/>
		</Box>
	);
});

const useWaveformData = (baseValue: number, oscillation: number, amplitude: number) =>
	useMemo(() => {
		const duration = oscillation > 0 ? Math.max(2, 3 / oscillation) : 5;
		const xData: number[] = [];
		const yData: number[] = [];

		for (let i = 0; i < 200; i++) {
			const t = (i / 199) * duration;
			xData.push(t);
			yData.push(waveValue(baseValue, amplitude, oscillation, t));
		}

		return { xData, yData };
	}, [baseValue, oscillation, amplitude]);

const waveValue = (baseValue: number, amplitude: number, oscillation: number, t: number) =>
	baseValue + amplitude * Math.sin(2 * Math.PI * oscillation * t);

export default EditedLiveTagValuePreview;